import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GUEST, SDTD, UNIT } from "@/components/people/roles";

const NONE = "none";

const Pick = ({ label, value, onChange, options, testId, placeholder }) => (
  <label className="block">
    <span className="label-caps">{label}</span>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="mt-1 h-10 rounded-none" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 rounded-none bg-popover">
        {options.map(([v, l]) => (
          <SelectItem key={v} value={v} data-testid={`${testId}-option-${v.replace(/\s+/g, "-")}`}>
            {l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </label>
);

const Chips = ({ label, plants, picked, onToggle, testId }) => (
  <div>
    <span className="label-caps">{label}</span>
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`${testId}-units`}>
      {plants.map((p) => (
        <button
          key={p.code}
          type="button"
          title={p.name}
          data-testid={`${testId}-unit-${p.code}`}
          onClick={() => onToggle(p.code)}
          className={`border px-2 py-1 font-data text-[11px] transition-colors duration-200 ${
            picked.includes(p.code)
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border hover:border-primary"
          }`}
        >
          {p.code}
        </button>
      ))}
    </div>
  </div>
);

/** An SDTD officer covers a set of units: the first is kept as the card's home. */
const Covered = ({ plants, value, set, testPrefix }) => {
  const covered = [value.home, ...value.units].filter(Boolean);
  const toggle = (code) => {
    const next = covered.includes(code) ? covered.filter((u) => u !== code) : [...covered, code];
    set({ home: next[0] || "", units: next.slice(1) });
  };
  return <Chips label="Units they cover *" plants={plants} picked={covered} onToggle={toggle} testId={testPrefix} />;
};

/** Team, role in the team, the units, and — for an invited member — the access picked for them. */
export const TeamPlacement = ({ catalog, plants, teams, guestAccess = [], value, onChange, testPrefix }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const roles = catalog[value.team] || [];
  const picked = roles.find((r) => r.role === value.team_role);
  const guest = value.team === GUEST;
  const access = guestAccess.find((a) => a.key === (value.access_role || "viewer"));
  const toggle = (code) =>
    set({ units: value.units.includes(code) ? value.units.filter((u) => u !== code) : [...value.units, code] });
  const pickTeam = (team) => {
    const only = (catalog[team] || []).length === 1 ? catalog[team][0].role : "";
    set({ team, team_role: only, access_role: "" });
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Pick label="Team *" value={value.team} onChange={pickTeam} options={teams.map((t) => [t, t])} testId={`${testPrefix}-team`} />
        <Pick
          label="Role in the team *"
          value={value.team_role}
          onChange={(team_role) => set({ team_role })}
          options={roles.map((r) => [r.role, r.role])}
          testId={`${testPrefix}-role`}
          placeholder="Choose the role"
        />
      </div>
      {picked && (
        <p className="border-l-2 border-primary bg-muted px-3 py-2 text-xs" data-testid={`${testPrefix}-access-default`}>
          {picked.help}.{" "}
          {guest ? (
            <>On the portal they get <b>{access?.label || "Viewer"}</b> access — picked below for this person.
              {access?.key === "admin" && " An SDTD Monitor with no unit picked watches the whole of SAIL."}</>
          ) : (
            <>On the portal they get <b>{picked.access_label}</b> access — it follows the role.</>
          )}
        </p>
      )}
      {guest && (
        <Pick
          label="Access on the portal *"
          value={value.access_role || "viewer"}
          onChange={(access_role) => set({ access_role })}
          options={guestAccess.map((a) => [a.key, a.label])}
          testId={`${testPrefix}-guest-access`}
        />
      )}
      {value.team === SDTD ? (
        <Covered plants={plants} value={value} set={set} testPrefix={testPrefix} />
      ) : (
        <>
          <Pick
            label={value.team === UNIT ? "Home unit *" : "Home unit"}
            value={value.home || NONE}
            onChange={(home) => set({ home: home === NONE ? "" : home, units: value.units.filter((u) => u !== home) })}
            options={[
              ...(value.team === UNIT ? [] : [[NONE, "None — not tied to a unit"]]),
              ...plants.map((p) => [p.code, `${p.code} — ${p.name}`]),
            ]}
            testId={`${testPrefix}-home`}
            placeholder="Choose the unit"
          />
          {(value.team === UNIT || guest) && (
            <Chips
              label={guest ? "Other units they may see" : "Other units they also cover"}
              plants={plants.filter((p) => p.code !== value.home)}
              picked={value.units}
              onToggle={toggle}
              testId={testPrefix}
            />
          )}
        </>
      )}
    </div>
  );
};
